import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { CheckCircle, MapPin, Truck, Wallet, ShieldCheck, Zap, Users, TrendingUp } from "lucide-react";

interface Benefit {
    title: string;
    description: string;
    icon: React.ElementType;
    colSpan?: 1 | 2;
}

const clientBenefits: Benefit[] = [
    {
        title: "Post Jobs for Free",
        description: "No upfront costs. Just detail your cargo.",
        icon: CheckCircle,
        colSpan: 1,
    },
    {
        title: "Real-Time Tracking",
        description: "Live GPS updates from pickup to drop-off.",
        icon: MapPin,
        colSpan: 1,
    },
    {
        title: "Verified Transporters",
        description: "Every driver is vetted for safety and reliability.",
        icon: ShieldCheck,
        colSpan: 2,
    },
];

const transporterBenefits: Benefit[] = [
    {
        title: "Instant Job Access",
        description: "Browse a live feed of high-value loads.",
        icon: Zap,
        colSpan: 2,
    },
    {
        title: "Secure Payments",
        description: "Guaranteed payouts upon successful delivery.",
        icon: Wallet,
        colSpan: 1,
    },
    {
        title: "Grow Your Fleet",
        description: "Consistent work to keep your trucks moving.",
        icon: Truck,
        colSpan: 1,
    },
];

const fleetManagerBenefits: Benefit[] = [
    {
        title: "Manage Multiple Vehicles",
        description: "Register and track your entire fleet from one dashboard.",
        icon: Truck,
        colSpan: 1,
    },
    {
        title: "Driver Management",
        description: "Invite drivers with codes and assign jobs efficiently.",
        icon: Users,
        colSpan: 1,
    },
    {
        title: "Centralized Payments",
        description: "All earnings in one wallet. Manage driver payouts easily.",
        icon: Wallet,
        colSpan: 2,
    },
];

const fleetDriverBenefits: Benefit[] = [
    {
        title: "Join Established Fleets",
        description: "Quick registration with your fleet manager's invite code.",
        icon: Users,
        colSpan: 2,
    },
    {
        title: "Automatic Tracking",
        description: "System-enforced GPS tracking for your protection.",
        icon: MapPin,
        colSpan: 1,
    },
    {
        title: "Transparent Earnings",
        description: "Track all completed jobs and payments clearly.",
        icon: TrendingUp,
        colSpan: 1,
    },
];

const RoleSwitcher = () => {
    const [role, setRole] = useState<"CLIENT" | "TRANSPORTER" | "FLEET_MANAGER" | "FLEET_DRIVER">("CLIENT");

    const getBenefits = () => {
        switch (role) {
            case "CLIENT": return clientBenefits;
            case "TRANSPORTER": return transporterBenefits;
            case "FLEET_MANAGER": return fleetManagerBenefits;
            case "FLEET_DRIVER": return fleetDriverBenefits;
        }
    };

    const getRoleColor = () => {
        switch (role) {
            case "CLIENT": return "bg-primary";
            case "TRANSPORTER": return "bg-accent";
            case "FLEET_MANAGER": return "bg-green-600";
            case "FLEET_DRIVER": return "bg-blue-600";
        }
    };

    const getRoleBorderColor = () => {
        switch (role) {
            case "CLIENT": return "border-primary";
            case "TRANSPORTER": return "border-accent";
            case "FLEET_MANAGER": return "border-green-600";
            case "FLEET_DRIVER": return "border-blue-600";
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto space-y-12">
            {/* Toggle Controls */}
            <div className="flex justify-center">
                <div className="bg-secondary/20 p-2 rounded-full flex flex-wrap gap-2 backdrop-blur-sm border border-secondary/20 justify-center">
                    <Button
                        variant={role === "CLIENT" ? "default" : "ghost"}
                        onClick={() => setRole("CLIENT")}
                        className="rounded-full px-6 py-4 text-base transition-all duration-300 relative overflow-hidden"
                    >
                        {role === "CLIENT" && (
                            <motion.div
                                layoutId="activeRole"
                                className="absolute inset-0 bg-primary z-0"
                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-10">Client</span>
                    </Button>
                    <Button
                        variant={role === "TRANSPORTER" ? "default" : "ghost"}
                        onClick={() => setRole("TRANSPORTER")}
                        className="rounded-full px-6 py-4 text-base transition-all duration-300 relative overflow-hidden"
                    >
                        {role === "TRANSPORTER" && (
                            <motion.div
                                layoutId="activeRole"
                                className="absolute inset-0 bg-accent z-0"
                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-10">Transporter</span>
                    </Button>
                    <Button
                        variant={role === "FLEET_MANAGER" ? "default" : "ghost"}
                        onClick={() => setRole("FLEET_MANAGER")}
                        className="rounded-full px-6 py-4 text-base transition-all duration-300 relative overflow-hidden"
                    >
                        {role === "FLEET_MANAGER" && (
                            <motion.div
                                layoutId="activeRole"
                                className="absolute inset-0 bg-green-600 z-0"
                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-10">Fleet Manager</span>
                    </Button>
                    <Button
                        variant={role === "FLEET_DRIVER" ? "default" : "ghost"}
                        onClick={() => setRole("FLEET_DRIVER")}
                        className="rounded-full px-6 py-4 text-base transition-all duration-300 relative overflow-hidden"
                    >
                        {role === "FLEET_DRIVER" && (
                            <motion.div
                                layoutId="activeRole"
                                className="absolute inset-0 bg-blue-600 z-0"
                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-10">Fleet Driver</span>
                    </Button>
                </div>
            </div>

            {/* Bento Grid Content */}
            <div className="min-h-[400px]">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={role}
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -20 }}
                        transition={{ duration: 0.4 }}
                        className="grid grid-cols-1 md:grid-cols-3 gap-6"
                    >
                        {getBenefits().map((benefit, index) => (
                            <motion.div
                                key={benefit.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className={`glass p-8 rounded-2xl flex flex-col justify-between hover-lift-more border-l-4 ${getRoleBorderColor()} ${benefit.colSpan === 2 ? 'md:col-span-2' : ''}`}
                            >
                                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 text-white shadow-lg ${getRoleColor()}`}>
                                    <benefit.icon className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold mb-2">{benefit.title}</h3>
                                    <p className="text-muted-foreground text-lg">{benefit.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default RoleSwitcher;
